import { useMemo, useState } from 'react';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import { useTheme as useMuiTheme, alpha } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import { useMe } from 'core/me/provider/MeProvider';
import PlanRequestModal from './PlanRequestModal';
import { sendPlanRequest } from './api/abonelikApi';

/* ---------------- utils ---------------- */
function formatDate(d) {
  if (!d) return '—';
  try {
    return new Intl.DateTimeFormat('tr-TR').format(new Date(d));
  } catch {
    return d;
  }
}

function moneyTry(x) {
  if (typeof x !== 'number') return '—';
  try {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(x);
  } catch {
    return `${x} ₺`;
  }
}

/* ---------------- small atoms ---------------- */
function Pill({ children, tone = 'default' }) {
  const theme = useMuiTheme();

  const bg =
    tone === 'info'
      ? alpha(theme.palette.info.main, 0.12)
      : tone === 'success'
      ? alpha(theme.palette.success.main, 0.12)
      : tone === 'warning'
      ? alpha(theme.palette.warning.main, 0.14)
      : alpha(theme.palette.text.primary, 0.08);

  const fg =
    tone === 'info'
      ? theme.palette.info.main
      : tone === 'success'
      ? theme.palette.success.main
      : tone === 'warning'
      ? theme.palette.warning.main
      : theme.palette.text.primary;

  return (
    <MDBox
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1,
        py: 0.35,
        borderRadius: '999px',
        fontSize: '0.65rem',
        fontWeight: 800,
        letterSpacing: '0.1em',
        backgroundColor: bg,
        color: fg,
        border: `1px solid ${alpha(fg, 0.18)}`,
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </MDBox>
  );
}

function FeatureRow({ text }) {
  return (
    <MDBox display="flex" alignItems="center" gap={0.8} sx={{ minHeight: 26 }}>
      <MDBox
        sx={{
          width: 22,
          height: 22,
          borderRadius: '8px',
          display: 'grid',
          placeItems: 'center',
          backgroundColor: 'rgba(0,0,0,0.06)',
          flex: '0 0 auto',
        }}
      >
        <Icon sx={{ fontSize: 16, opacity: 0.85 }}>check</Icon>
      </MDBox>
      <MDTypography variant="button" sx={{ opacity: 0.9, lineHeight: 1.2 }}>
        {text}
      </MDTypography>
    </MDBox>
  );
}

/* ---------------- PlanCard ---------------- */
function PlanCard({ plan, isCurrent, canManage, onOpenRequest }) {
  const theme = useMuiTheme();

  const isPro = plan.key === 'PRO';
  const isEnterprise = plan.key === 'ENTERPRISE';

  const borderColor = isCurrent
    ? theme.palette.success.main
    : isPro
    ? theme.palette.info.main
    : theme.palette.divider;

  const headerBg = isPro
    ? `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.18)}, ${alpha(
        theme.palette.primary.main,
        0.12
      )})`
    : 'transparent';

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: '18px',
        overflow: 'hidden',
        border: `1px solid ${alpha(borderColor, 0.85)}`,
        boxShadow: isCurrent ? 10 : 2,
        transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: isCurrent ? 12 : 8,
          borderColor: alpha(borderColor, 1),
        },
      }}
    >
      {/* Header */}
      <MDBox
        sx={{
          p: 2,
          background: headerBg,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
        }}
      >
        <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          <MDBox sx={{ minWidth: 0 }}>
            <MDTypography
              fontWeight="bold"
              sx={{
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={plan.title}
            >
              {plan.title}
            </MDTypography>

            <MDTypography variant="button" color="text" sx={{ opacity: 0.75, mt: 0.25 }}>
              {plan.desc}
            </MDTypography>
          </MDBox>

          <MDBox display="flex" flexDirection="column" alignItems="flex-end" gap={0.6}>
            {plan.highlight ? <Pill tone="info">ÖNERİLEN</Pill> : null}
            {isCurrent ? <Pill tone="success">AKTİF</Pill> : null}
          </MDBox>
        </MDBox>

        <MDBox mt={1.5} display="flex" alignItems="baseline" gap={1} flexWrap="wrap">
          <MDTypography fontWeight="bold" sx={{ fontSize: '1.35rem', lineHeight: 1.1 }}>
            {plan.priceText}
          </MDTypography>
          {plan.subText ? (
            <MDTypography variant="caption" sx={{ opacity: 0.75 }}>
              {plan.subText}
            </MDTypography>
          ) : null}
        </MDBox>
      </MDBox>

      {/* Body */}
      <MDBox p={2}>
        <MDTypography variant="caption" sx={{ opacity: 0.7, letterSpacing: '0.08em' }}>
          ÖZELLİKLER
        </MDTypography>

        <MDBox mt={1} display="flex" flexDirection="column" gap={0.6}>
          {plan.features.map((f) => (
            <FeatureRow key={f} text={f} />
          ))}
        </MDBox>

        <Divider sx={{ my: 1.75 }} />

        {/* ✅ BUTONLAR */}
        <MDBox display="flex" gap={1}>
          {isCurrent ? (
            <MDButton
              fullWidth
              variant="outlined"
              color="success"
              disabled
              sx={{ borderRadius: '14px', py: 1.05, textTransform: 'none', fontWeight: 800 }}
            >
              Mevcut Paketim
            </MDButton>
          ) : isPro ? (
            <MDButton
              fullWidth
              variant="gradient"
              color="info"
              disabled={!canManage}
              onClick={() => onOpenRequest('PRO')}
              sx={{ borderRadius: '14px', py: 1.05, textTransform: 'none', fontWeight: 800 }}
            >
              Pro’ya Geç
            </MDButton>
          ) : isEnterprise ? (
            <MDButton
              fullWidth
              variant="gradient"
              color="secondary"
              disabled={!canManage}
              onClick={() => onOpenRequest('ENTERPRISE')}
              sx={{ borderRadius: '14px', py: 1.05, textTransform: 'none', fontWeight: 800 }}
            >
              Enterprise Talep Et
            </MDButton>
          ) : (
            <MDButton
              fullWidth
              variant="outlined"
              color="secondary"
              disabled
              sx={{ borderRadius: '14px', py: 1.05, textTransform: 'none', fontWeight: 800 }}
            >
              Deneme
            </MDButton>
          )}
        </MDBox>

        {!canManage ? (
          <MDTypography variant="caption" sx={{ opacity: 0.75, mt: 1, display: 'block' }}>
            Plan değiştirmek için OWNER/ADMIN yetkisi gerekir.
          </MDTypography>
        ) : null}
      </MDBox>
    </Card>
  );
}

/* ---------------- Page ---------------- */
export default function SubscriptionPlansPage() {
  const theme = useMuiTheme();
  const { me } = useMe();

  const currentPlan = (me?.plan || 'TRIAL').toString();
  const canManage = me?.membershipRole === 'OWNER' || me?.membershipRole === 'ADMIN';

  const companyTitle = me?.companyName
    ? String(me.companyName).trim().toLocaleUpperCase('tr-TR')
    : '—';

  const planStatusTone =
    me?.planStatus === 'ACTIVE' ? 'success' : me?.planStatus === 'PAST_DUE' ? 'warning' : 'default';

  // ✅ modal state
  const [reqOpen, setReqOpen] = useState(false);
  const [reqPlan, setReqPlan] = useState(null); // 'PRO' | 'ENTERPRISE'

  function openRequest(planKey) {
    setReqPlan(planKey);
    setReqOpen(true);
  }

  async function handleRequestSubmit(form) {
    const payload = {
      planKey: String(form.planKey || ''),
      fullName: String(form.fullName || ''),
      companyName: String(form.companyName || ''),
      phone: String(form.phone || ''),
      note: String(form.note || ''),
    };

    await sendPlanRequest(payload);
  }

  const plans = useMemo(
    () => [
      {
        key: 'TRIAL',
        title: 'DENEME',
        priceText: 'ÜCRETSİZ',
        desc: 'Sistemi deneyin, temel yönetimi test edin.',
        features: [
          'Üye Yönetimi (Sınırlı)',
          'Takvim & Seans Oluşturma',
          'Paket Tanımlama',
          'Manuel Yoklama',
          'Temel Finans Takibi',
          'Sınırlı SMS Gönderimi',
          'Temel İşletme Raporları',
          'Tek Eğitmen Yetkilendirme',
          'E-posta Destek',
        ],
        subText: '14 gün ücretsiz erişim',
      },
      {
        key: 'PRO',
        title: 'PRO',
        priceText: `${moneyTry(18000)} / yıl`,
        desc: 'Büyüyen stüdyolar için eksiksiz yönetim paketi.',
        features: [
          'Sınırsız Üye ve Eğitmen',
          'Gelişmiş Finans & Tahsilat Yönetimi',
          'Otomatik Seans Hatırlatma (SMS)',
          'Paket Yenileme & Ödeme Uyarıları',
          'Gelişmiş Yoklama & Katılım Analizi',
          'Eğitmen Maaş & Prim Hesaplama',
          'Detaylı İşletme Raporları',
          'Öncelikli Müşteri Desteği',
          'Kurumsal Web Sitesi Dahil',
        ],
        highlight: true,
        subText: 'Aylık Ödeme = 2.000 ₺',
      },
      {
        key: 'ENTERPRISE',
        title: 'ENTERPRISE',
        priceText: 'ÖZEL TEKLİF',
        desc: 'Çok şubeli ve kurumsal yapılar için ölçeklenebilir çözüm.',
        features: [
          'Sınırsız Şube & Merkezi Yönetim',
          'Rol Bazlı Gelişmiş Yetkilendirme',
          'Özel API & Entegrasyon Desteği',
          'Kurumsal Raporlama & Veri Analitiği',
          'Gelişmiş Güvenlik & Veri İzolasyonu',
          'Dedicated Hesap Yöneticisi',
          'Öncelikli Destek & SLA',
          'Özel Özellik Geliştirme Opsiyonu',
          'Sunucu & Performans Optimizasyonu',
        ],
        subText: 'Kurumsal çözüm',
      },
    ],
    []
  );

  return (
    <MDBox>
      {/* Header */}
      <MDBox
        sx={{
          p: 2,
          borderRadius: '18px',
          border: `1px solid ${theme.palette.divider}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)}, ${alpha(
            theme.palette.primary.main,
            0.04
          )})`,
          mb: 2,
        }}
      >
        <MDBox
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1.5}
        >
          <MDBox>
            <MDTypography variant="h5" fontWeight="bold" sx={{ letterSpacing: '0.06em' }}>
              ABONELİK
            </MDTypography>
            <MDTypography variant="button" sx={{ opacity: 0.8 }}>
              {companyTitle} • Planını görüntüle ve yönet.
            </MDTypography>

            <MDBox mt={1.2} display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Pill tone="info">{String(currentPlan).toLocaleUpperCase('tr-TR')}</Pill>
              <Pill tone={planStatusTone}>
                {(me?.planStatus ?? '—').toString().toLocaleUpperCase('tr-TR')}
              </Pill>
              <MDTypography variant="caption" sx={{ opacity: 0.75 }}>
                {formatDate(me?.planStartAt)} → {formatDate(me?.planEndAt)}
                {typeof me?.trialDaysLeft === 'number' && currentPlan === 'TRIAL'
                  ? ` • ${me.trialDaysLeft} gün kaldı`
                  : ''}
              </MDTypography>
            </MDBox>
          </MDBox>

          <MDButton
            variant="outlined"
            color="secondary"
            disabled={!canManage}
            onClick={() => console.log('manage billing')}
            sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 700 }}
          >
            Ödeme/Abonelik Yönet
          </MDButton>
        </MDBox>
      </MDBox>

      {/* Plans */}
      <Grid container spacing={2}>
        {plans.map((p) => (
          <Grid key={p.key} item xs={12} md={4}>
            <PlanCard
              plan={p}
              isCurrent={currentPlan === p.key}
              canManage={canManage}
              onOpenRequest={openRequest}
            />
          </Grid>
        ))}
      </Grid>

      {/* Modal */}
      <PlanRequestModal
        open={reqOpen}
        onClose={() => setReqOpen(false)}
        me={me}
        planKey={reqPlan}
        onSubmit={handleRequestSubmit}
      />
    </MDBox>
  );
}
